export type CenterAndZoomObject = {
  longitude: number;
  latitude: number;
  zoom: number;
};

/**
 * Get the center point and zoom factor for a static map.
 * - The center point is the average of the min and max latitudes and longitudes.
 * - The zoom factor ensures all location pins will be visible on the map.
 * @param locations - The locations to get the center point and zoom factor for.
 * @param width - The width of the map.
 * @param height - The height of the map.
 * @param returnAsObject - Whether to return the result as an object or a string.
 * @returns The center point and zoom factor for the map.
 */
export const getCenterAndZoom = (
  locations: {
    latitude: number;
    longitude: number;
  }[],
  width: number,
  height: number,
  returnAsObject = false,
): CenterAndZoomObject | string => {
  const longitudes = locations.map(location => location.longitude);
  const minLongitude = Math.min(...longitudes);
  const maxLongitude = Math.max(...longitudes);
  const centerLongitude = (minLongitude + maxLongitude) / 2;

  const latitudes = locations.map(location => location.latitude);
  const minLatitude = Math.min(...latitudes);
  const maxLatitude = Math.max(...latitudes);
  const centerLatitude = (minLatitude + maxLatitude) / 2;

  const zoom = getZoom(
    minLatitude,
    maxLatitude,
    minLongitude,
    maxLongitude,
    width,
    height,
  );

  return returnAsObject
    ? {
        longitude: centerLongitude,
        latitude: centerLatitude,
        zoom: Math.min(zoom, 16), // Avoid super zoomed in maps
      }
    : `${centerLongitude},${centerLatitude},${zoom}`;
};

/**
 * Get the zoom factor for a static map.
 * @param minLat - The minimum latitude.
 * @param maxLat - The maximum latitude.
 * @param minLon - The minimum longitude.
 * @param maxLon - The maximum longitude.
 * @param widthPx - The width of the map.
 * @param heightPx - The height of the map.
 * @returns The zoom factor for the map.
 */
const getZoom = (
  minLat: number,
  maxLat: number,
  minLon: number,
  maxLon: number,
  widthPx: number,
  heightPx: number,
) => {
  const WORLD_DIM = { height: 512, width: 512 }; // tile size base
  const ZOOM_MAX = 22;

  const latFraction = (latRad(maxLat) - latRad(minLat)) / Math.PI;
  const lonDiff = maxLon - minLon;
  const lonFraction = (lonDiff < 0 ? lonDiff + 360 : lonDiff) / 360;

  const latZoom = Math.log2(heightPx / WORLD_DIM.height / latFraction);
  const lonZoom = Math.log2(widthPx / WORLD_DIM.width / lonFraction);

  return Math.min(Math.floor(Math.min(latZoom, lonZoom)), ZOOM_MAX);
};

/**
 * Convert a latitude to radians.
 * @param lat - The latitude to convert to radians.
 * @returns The latitude in radians.
 */
const latRad = (lat: number) => {
  const sin = Math.sin((lat * Math.PI) / 180);
  const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
  return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
};
